import React, { useState, lazy, Suspense, useContext, useEffect } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { FiBookOpen, FiEdit3 } from 'react-icons/fi';
import { useMemo } from 'react';
import './CuadernoDigital.scss';
import './Components/EstimulacionNeuronal.css';
import { useLocation } from 'react-router-dom';
import NavbarGlobal from './NavbarGlobal';
import { AppContext } from '../Context';

const EvaluacionBasica = lazy(() => import('./EvaluacionBasica'));
const EvaluacionIntermedia = lazy(() => import('./EvaluacionIntermedia'));
const EvaluacionAvanzada = lazy(() => import('./EvaluacionAvanzada'));

const ETIQUETAS_NIVEL = {
  Basico: 'Básico',
  Intermedio: 'Intermedio',
  Avanzado: 'Avanzado',
};

const CuadernoDigital = () => {
  const location = useLocation();
  const [nivelSeleccionado, setNivelSeleccionado] = useState('');
  const { level, teacher, currentUser } = useContext(AppContext);

  const allowedLevels = useMemo(() => {
    if (teacher || currentUser?.isMaster) return ['Basico', 'Intermedio', 'Avanzado'];
    
    const n = parseInt(level, 10);
    const levelsMap = {
      1: ['Basico'],
      2: ['Basico', 'Intermedio'],
      3: ['Basico', 'Intermedio', 'Avanzado']
    };

    return levelsMap[n] || [];
  }, [currentUser?.isMaster, level, teacher]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const qNivel = params.get('nivel');
    if (qNivel && allowedLevels.includes(qNivel)) {
      setNivelSeleccionado(qNivel);
      return;
    }

    if (!nivelSeleccionado && allowedLevels.length > 0) {
      setNivelSeleccionado(allowedLevels[0]);
    } else if (nivelSeleccionado && !allowedLevels.includes(nivelSeleccionado)) {
      setNivelSeleccionado(allowedLevels[0]);
    }
  }, [allowedLevels, location.search, nivelSeleccionado]);

  const renderEvaluacion = () => {
    switch (nivelSeleccionado) {
      case 'Basico':
        return <EvaluacionBasica />;
      case 'Intermedio':
        return <EvaluacionIntermedia />;
      case 'Avanzado':
        return <EvaluacionAvanzada />;
      default:
        return <p className="select-level-message">Seleccione un nivel para mostrar las evaluaciones.</p>;
    }
  };

  return (
    <div className="cuaderno-digital-page">
      <NavbarGlobal title="Cuaderno Digital" showBackButton={true} />

      <Container className="pb-5">
        <section className="cuaderno-header" aria-labelledby="cuaderno-title">
          <div className="cuaderno-header__copy">
            <span className="cuaderno-header__eyebrow">
              <FiBookOpen aria-hidden="true" /> Evaluaciones por nivel
            </span>
            <h1 id="cuaderno-title" className="cuaderno-title">Tu cuaderno de aprendizaje</h1>
            <p className="cuaderno-subtitle">
              Elige un nivel y continúa los ejercicios que acompañan tu proceso de lectura.
            </p>
          </div>
          <div className="cuaderno-header__mark" aria-live="polite">
            <FiEdit3 aria-hidden="true" />
            <strong>{ETIQUETAS_NIVEL[nivelSeleccionado] || '—'}</strong>
            <span>{nivelSeleccionado ? 'Nivel activo' : 'Selecciona un nivel'}</span>
          </div>
        </section>

        <Row className="justify-content-center mb-4">
          <Col lg={8}>
            <div className="level-selector__heading">
              <span>Tu recorrido</span>
              <strong>Selecciona el nivel de evaluación</strong>
            </div>
            <div className="level-selector">
              {allowedLevels.map((nivel) => {
                const isActive = nivelSeleccionado === nivel;
                return (
                  <Button
                    key={nivel}
                    type="button"
                    className={`evaluation-button ${isActive ? 'active' : ''}`}
                    onClick={() => setNivelSeleccionado(nivel)}
                  >
                    <span>{ETIQUETAS_NIVEL[nivel] || nivel}</span>
                    {isActive && (
                      <span className="evaluation-button__check" aria-hidden="true">
                        <span />
                      </span>
                    )}
                  </Button>
                );
              })}
            </div>
          </Col>
        </Row>

        <Suspense fallback={<div className="loading-message">Cargando evaluación...</div>}>
          {renderEvaluacion()}
        </Suspense>
      </Container>
    </div>
  );
};

export { CuadernoDigital };
